import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEvalCostEstimate } from '@/hooks/useEvalCostEstimate'
import type { TriggerEvalRunPayload } from '@/lib/api/eval.client'
import { EvalPromptType, EVAL_PROMPT_TYPE_LABELS } from '@super/shared-types'

interface Props {
  open: boolean
  onClose: () => void
  onTrigger: (payload: TriggerEvalRunPayload) => Promise<void>
  companyId?: string
  companyName?: string
}

export function TriggerRunModal({
  open,
  onClose,
  onTrigger,
  companyId,
  companyName,
}: Props) {
  const [promptType, setPromptType] = useState<EvalPromptType>(
    EvalPromptType.PROPERTY_MATCHING,
  )
  const { estimate, isFetching: isEstimating } = useEvalCostEstimate({
    companyId,
    promptType,
  })

  function reset() {
    setPromptType(EvalPromptType.PROPERTY_MATCHING)
  }

  function handleConfirm() {
    void onTrigger({ companyId, promptType })
    reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Run evaluation
            {companyName ? (
              <span className="text-muted-foreground font-normal">
                {' '}
                · {companyName}
              </span>
            ) : (
              <span className="text-muted-foreground font-normal">
                {' '}
                · Global
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>Prompt type</Label>
            <Select
              value={promptType}
              onValueChange={v => setPromptType(v as EvalPromptType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(EvalPromptType).map(pt => (
                  <SelectItem key={pt} value={pt}>
                    {EVAL_PROMPT_TYPE_LABELS[pt]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md bg-muted p-4 text-sm flex flex-col gap-1">
            {isEstimating ? (
              <span className="text-muted-foreground">Estimating…</span>
            ) : estimate ? (
              <>
                <span className="font-medium">
                  {estimate.caseCount}{' '}
                  {estimate.caseCount === 1 ? 'case' : 'cases'}{' '}
                  <span className="font-normal text-muted-foreground">
                    ({companyName ?? 'global'})
                  </span>
                </span>
                <span className="text-muted-foreground">
                  ~
                  {(
                    (estimate.estimatedPromptTokens ?? 0) +
                    (estimate.estimatedCompletionTokens ?? 0)
                  ).toLocaleString()}{' '}
                  tokens
                </span>
                <span className="text-muted-foreground">
                  Estimated cost:{' '}
                  <span className="font-mono">
                    ${(estimate.estimatedCostUsd ?? 0).toFixed(4)}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">
                No cases found for this scope.
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!estimate || estimate.caseCount === 0}
          >
            Run evaluation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
